/**
 * Hostnames that ARE an application — the Domains screen's whole API.
 *
 * Everything here is administration on the PLATFORM host: a project domain
 * serves the running application, not the screen that configures it (the
 * backend refuses these endpoints there — see `middleware/domain_scope`).
 */

import api from './client'

export interface ProjectDomain {
  id: number
  hostname: string
  project: string
  owner_user_id: string
  tenant_id: string
  /** 'app' — the running application; 'site' — a published public site. */
  target: 'app' | 'site'
  site_slug: string | null
  status: 'pending' | 'active' | 'disabled'
  is_primary: boolean
  /** The address the platform minted for this project — renameable in place,
   *  with nothing to verify, because its zone is the platform's own. */
  is_default: boolean
  /** Just the label of a default address (`sales-orders`), for the rename box. */
  label: string
  verification_token: string
  verification_method: string | null
  verified_at: string | null
  last_checked_at: string | null
  /** Why the last DNS check did not pass — shown verbatim. */
  last_error: string | null
  url: string
  /** HTTPS state, from Instance Admin → Edge & HTTPS. */
  tls?: DomainTls
  dns: DnsRecord[]
}

export interface DomainTls {
  /** False when something outside the platform serves HTTPS (edge mode
   *  "external") or the domain is not live yet. */
  managed: boolean
  /** none | pending | issuing | active | failed | revoked | cloudflare |
   *  external | inactive | unknown */
  status: string
  source?: string
  not_after?: string | null
  last_error?: string | null
  next_attempt_at?: string | null
  /** Cloudflare's own certificate status in Cloudflare mode. */
  cloudflare_ssl_status?: string | null
  /** Has this domain's latest change reached every registered web server? */
  propagation?: {
    servers_total: number
    servers_ok: number
    required_revision: number
    servers: { name: string; ok: boolean; status: string; error: string | null }[]
  }
}

export interface DnsRecord {
  kind: 'CNAME' | 'A' | 'TXT'
  name: string
  value: string
  purpose: 'traffic' | 'verification'
}

export interface DomainConfig {
  /** The host customers CNAME to, or ''. */
  cname: string
  /** A-record targets for a zone apex. */
  ips: string[]
  /** A suffix this instance owns (names under it need no DNS proof). */
  suffix: string
  platform_host: string
  txt_prefix: string
  /** False when no administrator has set the edge up yet. */
  configured: boolean
}

export async function getDomainConfig(): Promise<DomainConfig> {
  const { data } = await api.get('/domains/config')
  return data
}

export interface ProjectDomainList {
  domains: ProjectDomain[]
  /** The zone the platform hands out names in (`.platform.twasta.ai`), or ''
   *  on an instance with no public name of its own. */
  zone: string
  /** Slugs of every X (External Website) node in this project's navigation. */
  available_sites: string[]
}

export async function listDomains(project: string): Promise<ProjectDomainList> {
  const { data } = await api.get(`/domains/project/${encodeURIComponent(project)}`)
  return { domains: data.domains, zone: data.zone || '', available_sites: data.available_sites || [] }
}

/** Move the project's platform address to a different label. */
export async function renameDomain(id: number, label: string): Promise<ProjectDomain> {
  const { data } = await api.post(`/domains/${id}/hostname`, { label })
  return data.domain
}

export async function addDomain(project: string, body: {
  hostname: string
  target?: 'app' | 'site'
  site_slug?: string | null
  make_primary?: boolean
}): Promise<ProjectDomain> {
  const { data } = await api.post(
    `/domains/project/${encodeURIComponent(project)}`, body,
  )
  return data.domain
}

export async function verifyDomain(id: number): Promise<ProjectDomain> {
  const { data } = await api.post(`/domains/${id}/verify`)
  return data.domain
}

export async function makeDomainPrimary(id: number): Promise<ProjectDomain> {
  const { data } = await api.post(`/domains/${id}/primary`)
  return data.domain
}

export async function setDomainEnabled(id: number, enabled: boolean): Promise<ProjectDomain> {
  const { data } = await api.post(`/domains/${id}/enabled`, { enabled })
  return data.domain
}

export async function removeDomain(id: number): Promise<void> {
  await api.delete(`/domains/${id}`)
}

/** Ask for this domain's HTTPS certificate again now (it is issued within a
 *  minute by the platform's certificate leader). */
export async function retryDomainCertificate(id: number): Promise<ProjectDomain> {
  const { data } = await api.post(`/domains/${id}/certificate/retry`)
  return data.domain
}
